import { usePurchaseContext } from '~/purchase/context';
import { useI18n, Trans } from '~/common/context';
import { RecordValues } from '@wyz/types';
import { Button, Card, Container, Flex, Text, WizardSteps } from '@wyz/ui';
import { invariant } from '@wyz/utils';
import * as React from 'react';
import { QuoteApplicationStep, QuotePricingStep } from '~/purchase/components';
import { QuotePricingPolicyHelpers } from '~/purchase/helpers';
import { usePolicyFormStepper } from '~/purchase/hooks';
import {
  PRICING_STATUS_ENUM,
  QUOTE_PRICING_POLICY_FORM_STEPS,
} from '~/purchase/schemas';
import {
  IGlobalQuotePricingFormData,
  IPricingStatus,
  QuotePricingPolicyFormStep,
} from '~/purchase/types';
type Step = RecordValues<
  typeof QuotePricingPolicyHelpers.QUOTE_PRICING_POLICY_STEP_INDEX
>;

type Props = {
  onSubmit: (
    formData: IGlobalQuotePricingFormData,
    status: IPricingStatus,
  ) => void;
  isSubmitting: boolean;
  defaultValues: IGlobalQuotePricingFormData;
  policyGroupId?: number;
};
function QuotePricingPolicyForm({
  onSubmit,
  isSubmitting,
  defaultValues,
  policyGroupId,
}: Props) {
  const { t } = useI18n();
  const { state } = usePurchaseContext();
  invariant(state.targetCustomer);
  const [formState, setFormState] =
    React.useState<IGlobalQuotePricingFormData>(defaultValues);

  const [currentStepIndex, stepperHandlers] = usePolicyFormStepper<Step>({});
  const currentStepKey = QUOTE_PRICING_POLICY_FORM_STEPS[currentStepIndex];
  return (
    <>
      <WizardSteps
        steps={(
          Object.keys(
            QuotePricingPolicyHelpers.QUOTE_PRICING_POLICY_STEP_INDEX,
          ) as Array<QuotePricingPolicyFormStep>
        ).map((stepKey) => ({
          id: stepKey,
          label: t(
            QuotePricingPolicyHelpers.QUOTE_PRICING_POLICY_STEP_LABELS[stepKey],
          ),
        }))}
        activeStep={currentStepIndex}
        onSelectStep={() => {}} // no switch between step using the wizard, only the button prev/next should be used, otherwise, we won't validate the form correctly
      />
      <Container>
        {currentStepIndex ==
          QuotePricingPolicyHelpers.QUOTE_PRICING_POLICY_STEP_INDEX
            .APPLICATION && (
          <QuoteApplicationStep
            globalFormData={formState}
            policyGroupId={policyGroupId}
            customerId={state.targetCustomer.id}
            onSubmit={(formData) => {
              stepperHandlers.onNext();
              setFormState((prev) => ({
                ...prev,
                APPLICATION: formData,
              }));
            }}
          />
        )}
        {currentStepIndex ==
          QuotePricingPolicyHelpers.QUOTE_PRICING_POLICY_STEP_INDEX.PRICING && (
          <QuotePricingStep
            globalFormData={formState}
            onSubmit={(formData, status) => {
              setFormState((prev) => {
                const nextState = { ...prev, PRICING: formData };
                onSubmit(nextState, status);
                return nextState;
              });
            }}
            targetCustomer={state.targetCustomer}
            supplierIds={[state.targetCustomer.id]}
          />
        )}

        <Card className='sticky' style={{ bottom: '5%' }}>
          <Card.Body>
            <Flex justifyContent={'between'}>
              <Button
                type='button'
                id='previous'
                variant='link'
                disabled={
                  isSubmitting ||
                  currentStepIndex === QuotePricingPolicyHelpers.FIRST_STEP
                }
                onClick={stepperHandlers.onPrevious}
              >
                <Trans i18nKey='app.common.button.previous' />
              </Button>
              <Flex className='space-x-2' alignItems='center'>
                <Text heading='sm' color='secondary'>
                  {formState.PRICING?.name}
                </Text>
                <Text heading='sm' color='secondary-gray'>
                  <Trans
                    i18nKey='app.common.step_value'
                    values={{
                      stepNumber: currentStepIndex + 1,
                      stepName: t(
                        QuotePricingPolicyHelpers
                          .QUOTE_PRICING_POLICY_STEP_LABELS[currentStepKey],
                      ),
                    }}
                  />
                </Text>
                {currentStepIndex === QuotePricingPolicyHelpers.LAST_STEP && (
                  <Button
                    id={'save-as-draft'}
                    data-status={PRICING_STATUS_ENUM.DRAFT}
                    variant='secondary'
                    form={currentStepIndex.toString()}
                    type='submit'
                    isLoading={isSubmitting}
                    children={
                      <Trans i18nKey='app.common.button.save_as_draft' />
                    }
                  />
                )}
                <Button
                  id='next'
                  data-status={PRICING_STATUS_ENUM.ENABLED}
                  variant='primary'
                  type='submit'
                  form={currentStepIndex.toString()}
                  isLoading={isSubmitting}
                  children={
                    currentStepIndex < QuotePricingPolicyHelpers.LAST_STEP
                      ? t('app.common.buttons.continue')
                      : t('app.common.button.save')
                  }
                />
              </Flex>
            </Flex>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}
export default QuotePricingPolicyForm;
